import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Modal,
  Spin,
  Alert,
  Space,
  Select,
  Row,
  Col,
  Tooltip,
  Typography
} from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import {
  bonificationApi,
  BonificationReportDto,
  GetBonificationReportsParams,
  RejectionDetail
} from 'services/parametersService';

const { Option } = Select;
const { Text, Title } = Typography;

interface BonificationReportsProps {
  color: string;
}

const BonificationReports: React.FC<BonificationReportsProps> = ({ color }) => {
  const [reports, setReports] = useState<BonificationReportDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [rejectionDetails, setRejectionDetails] = useState<RejectionDetail[]>([]);
  const [rejectionLoading, setRejectionLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchReports();
      } catch (error) {
        console.error('Component error:', error);
      }
    };
    loadData();
  }, [statusFilter]);

  const fetchReports = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const params: GetBonificationReportsParams = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await bonificationApi.getBonificationReports(params);
      
      if (!response || response.data === null) {
        setReports([]); 
        return;
      }

      if (Array.isArray(response.data)) {
        const formattedReports = response.data.map((report: any) => ({
          ...report,
          totalLines: report.totalLines || 0,
          validated: report.validated || 0,
          rejected: report.rejected || 0,
          duration: report.duration || 'N/A'
        }));
        setReports(formattedReports);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des rapports:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setReports([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDownloadLog = async (jobId: number, fileName: string) => {
    try {
      const blob = await bonificationApi.downloadLogFile(jobId);
      const url = window.URL.createObjectURL(blob);

      const newWindow = window.open(url, '_blank');

      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `log_${fileName}_${jobId}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Erreur téléchargement log:', error);
      Modal.error({
        title: 'Erreur de téléchargement',
        content: 'Impossible de télécharger le fichier de log.',
      });
    }
  };

  const handleDownloadReject = async (jobId: number, fileName: string) => {
    try {
      const blob = await bonificationApi.downloadRejectFile(jobId);
      const url = window.URL.createObjectURL(blob);

      const newWindow = window.open(url, '_blank');

      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `rejections_${fileName}_${jobId}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Erreur téléchargement rejets:', error);
      Modal.error({
        title: 'Erreur de téléchargement',
        content: 'Impossible de télécharger le fichier de rejets.',
      });
    }
  };

  const handleViewRejections = async (jobId: number) => {
    try {
      setRejectionLoading(true);
      setSelectedJobId(jobId);
      setRejectionModalVisible(true);

      const response = await bonificationApi.getRejectionDetails(jobId);

      if (response && Array.isArray(response.data)) {
        const mappedDetails = response.data.map((item: any) => ({
          id: item.lineNumber,
          jobId: jobId,
          lineNumber: item.lineNumber,
          rejectionReason: item.reason,
          lineContent: item.rawData,
          rejectedAt: item.rejectedAt || new Date().toISOString(),
        }));
        setRejectionDetails(mappedDetails);
      } else {
        setRejectionDetails([]);
      }
    } catch (error) {
      console.error('Erreur récupération rejets:', error);
      Modal.error({
        title: 'Erreur',
        content: 'Impossible de récupérer les détails des rejets.',
      });
      setRejectionModalVisible(false);
    } finally {
      setRejectionLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      'COMPLETED': { color: 'green', text: 'Complété' },
      'RUNNING': { color: 'blue', text: 'En cours' },
      'ERROR': { color: 'red', text: 'Échoué' },
      'PENDING': { color: 'orange', text: 'En attente' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Job ID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: 80,
      sorter: (a: BonificationReportDto, b: BonificationReportDto) => a.jobId - b.jobId,
    },
    {
      title: 'Fichier',
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: 'Complété', value: 'COMPLETED' },
        { text: 'En cours', value: 'RUNNING' },
        { text: 'Échoué', value: 'ERROR' },
        { text: 'En attente', value: 'PENDING' },
      ],
      onFilter: (value: any, record: BonificationReportDto) => record.status === value,
    },
    {
      title: 'Utilisateur',
      dataIndex: 'user',
      key: 'user',
      width: 120,
    },
    {
      title: 'Lignes',
      key: 'lines',
      width: 200,
      render: (record: BonificationReportDto) => (
        <Space direction="vertical" size="small">
          <Text>
            <FileTextOutlined style={{ color: '#1890ff', marginRight: '4px' }} />
            Total: <strong>{(record.totalLines || 0).toLocaleString()}</strong>
          </Text>
          <Space size="small">
            <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
              <CheckCircleOutlined style={{ marginRight: '4px' }} />
              {(record.validated || 0).toLocaleString()}
            </Text>
            <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
              <CloseCircleOutlined style={{ marginRight: '4px' }} />
              {(record.rejected || 0).toLocaleString()}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Date d\'Import',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 150,
      render: (text: string) => new Date(text).toLocaleString('fr-FR'),
      sorter: (a: BonificationReportDto, b: BonificationReportDto) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    },
    {
      title: 'Durée',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (record: BonificationReportDto) => (
        <Space size="small">
          {record.rejected > 0 && (
            <Tooltip title="Voir les rejets">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewRejections(record.jobId)}
              />
            </Tooltip>
          )}

          {record.logFile && (
            <Tooltip title="Télécharger le log">
              <Button
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleDownloadLog(record.jobId, record.fileName)}
              />
            </Tooltip>
          )}

          {record.rejectFile && record.rejected > 0 && (
            <Tooltip title="Télécharger les rejets">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                danger
                onClick={() => handleDownloadReject(record.jobId, record.fileName)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const rejectionColumns = [
    {
      title: 'Ligne',
      dataIndex: 'lineNumber',
      key: 'lineNumber',
      width: 80,
      sorter: (a: RejectionDetail, b: RejectionDetail) => a.lineNumber - b.lineNumber,
    },
    {
      title: 'Raison du rejet',
      dataIndex: 'rejectionReason',
      key: 'rejectionReason',
      ellipsis: true,
    },
    {
      title: 'Contenu de la ligne',
      dataIndex: 'lineContent',
      key: 'lineContent',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text code>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Date de rejet',
      dataIndex: 'rejectedAt',
      key: 'rejectedAt',
      width: 150,
      render: (text: string) => new Date(text).toLocaleString('fr-FR'),
    },
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Chargement des rapports...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => fetchReports()}>
              Réessayer
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Historique des Bonifications
              </Title>
            </Col>
            <Col>
              <Space>
                <Text>Filtrer par statut:</Text>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">Tous</Option>
                  <Option value="COMPLETED">Complété</Option>
                  <Option value="RUNNING">En cours</Option>
                  <Option value="ERROR">Échoué</Option>
                  <Option value="PENDING">En attente</Option>
                </Select>
                <Button onClick={() => fetchReports(false)} loading={refreshing}>
                  Actualiser
                </Button>
              </Space>
            </Col>
          </Row>
        }
        style={{ position: 'relative' }}
      >
        {refreshing && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10
          }}>
            <Spin size="large" />
          </div>
        )}
        
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="jobId"
          pagination={{
            total: reports.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} éléments`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={`Détails des rejets - Job ${selectedJobId}`}
        open={rejectionModalVisible}
        onCancel={() => setRejectionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRejectionModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={1000}
      >
        {rejectionLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Alert
              message={`${rejectionDetails.length} ligne(s) rejetée(s)`}
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <Table
              columns={rejectionColumns}
              dataSource={rejectionDetails}
              rowKey="id"
              pagination={{
                pageSize: 5,
                showSizeChanger: false,
              }}
              size="small"
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default BonificationReports;